/**
 * Project settings dialog. Opened from:
 *  - Landing page (edit button on each recent-project card)
 *  - Editor top bar (gear icon while working on a project)
 *
 * Using the native <dialog> element with showModal(): focus trap, Escape to
 * close, and "modal" role-semantics all come for free. We don't reimplement
 * any of this with a custom overlay — less code, better a11y, lighter bundle.
 *
 * The body is a scrollable column with a fixed header/footer. Sections
 * (Deploy target / Asset origin) collapse via native <details> so the
 * dialog stays scannable as more sections are added.
 *
 * Mode-aware: in free mode the user types deploy/asset secrets inline
 * (they live in localStorage). In cloud mode the dialog only edits the
 * project name — the linked org-scoped target/origin reads are shown
 * read-only with a note pointing the user at org settings (the picker
 * lives in a separate slice).
 */

import type { AssetOriginConfig, DeployTargetConfig } from '@bliksys/shared';
import { type ReactNode, useEffect, useId, useMemo, useRef, useState } from 'react';

import { orgApi } from '../data/orgApi.js';
import { useProjectStore } from '../data/StoreContext.js';
import type { Project, ProjectCapabilities } from '../stores/types.js';
import { FULL_ACCESS, VersionConflictError } from '../stores/types.js';

import { AssetOriginField, labelAssetKind, validateAssetOrigin } from './AssetOriginField.js';
import { AssetOriginPicker } from './AssetOriginPicker.js';
import { DeployTargetField, labelDeployKind, validateDeployTarget } from './DeployTargetField.js';
import { DeployTargetPicker } from './DeployTargetPicker.js';
import { ProjectMembersPanel } from './ProjectMembersPanel.js';
import { toast } from './Toast.js';

import './ProjectSettings.css';

interface Props {
  project: Project;
  open: boolean;
  onClose: () => void;
  onSaved: (project: Project) => void;
}

interface FormState {
  name: string;
  /** Free-mode only. Inline deploy config that travels with the project. */
  target: DeployTargetConfig | undefined;
  /** Free-mode only. Inline asset origin config. */
  origin: AssetOriginConfig | undefined;
  /** Cloud-mode only. ID of the org-scoped target this project links to. */
  deployTargetId: string | null;
  /** Cloud-mode only. ID of the org-scoped asset origin. */
  assetOriginId: string | null;
}

interface SectionOpenState {
  deploy: boolean;
  asset: boolean;
}

export function ProjectSettings({ project, open, onClose, onSaved }: Props): JSX.Element {
  const store = useProjectStore();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [form, setForm] = useState<FormState>(() => ({
    name: project.name,
    target: project.deployTarget,
    origin: project.assetOrigin,
    deployTargetId: project.deployTargetId ?? null,
    assetOriginId: project.assetOriginId ?? null,
  }));
  // Sections auto-expand when the project already has them configured —
  // so a user opening settings can verify their existing setup at a
  // glance — and stay collapsed when empty so the dialog isn't a wall.
  const [sections, setSections] = useState<SectionOpenState>(() => ({
    deploy: project.deployTarget !== undefined,
    asset: project.assetOrigin !== undefined,
  }));
  const [error, setError] = useState<string | null>(null);
  // The caller's capabilities on this project. Free mode and the brief
  // window before the fetch resolves default to FULL_ACCESS (no flash of
  // hidden controls in single-user mode); cloud mode narrows it.
  const [caps, setCaps] = useState<ProjectCapabilities>(FULL_ACCESS);
  const [membersOpen, setMembersOpen] = useState(false);

  // Org-scoped API client for the member panel (cloud mode only).
  const api = useMemo(
    () => (store.orgSlug ? orgApi({ orgSlug: store.orgSlug }) : null),
    [store.orgSlug],
  );

  // Fetch capabilities when the dialog opens in cloud mode so the settings
  // form + member controls reflect the caller's role.
  useEffect(() => {
    if (!open) return;
    if (store.mode !== 'cloud' || !store.getCapabilities) {
      setCaps(FULL_ACCESS);
      return;
    }
    let cancelled = false;
    void store
      .getCapabilities(project.id)
      .then((c) => {
        if (!cancelled) setCaps(c);
      })
      .catch(() => {
        /* leave default; the server still enforces */
      });
    return () => {
      cancelled = true;
    };
  }, [open, project.id, store]);

  // Reset form whenever the dialog opens on a different project.
  useEffect(() => {
    if (open) {
      setForm({
        name: project.name,
        target: project.deployTarget,
        origin: project.assetOrigin,
        deployTargetId: project.deployTargetId ?? null,
        assetOriginId: project.assetOriginId ?? null,
      });
      setSections({
        deploy: project.deployTarget !== undefined,
        asset: project.assetOrigin !== undefined,
      });
      setError(null);
    }
  }, [open, project]);

  // Open/close the native dialog in response to the prop. Using showModal()
  // rather than the `open` attribute gives us modal semantics + focus trap.
  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    else if (!open && d.open) d.close();
  }, [open]);

  // Sync native close events (Esc, backdrop click on supported browsers)
  // back to React state.
  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    const handler = (): void => onClose();
    d.addEventListener('close', handler);
    return () => d.removeEventListener('close', handler);
  }, [onClose]);

  const handleSave = async (): Promise<void> => {
    const trimmed = form.name.trim();
    if (!trimmed) {
      setError('Name cannot be empty.');
      return;
    }
    // Validation only runs against fields the dialog actually exposes.
    // Cloud mode skips deploy/asset validation entirely — those fields
    // aren't editable here.
    if (store.mode === 'free') {
      const deployErrors = validateDeployTarget(form.target);
      const firstDeployError = Object.values(deployErrors)[0];
      if (firstDeployError) {
        setSections((s) => ({ ...s, deploy: true }));
        setError(`Deploy target: ${firstDeployError}`);
        return;
      }
      const originErrors = validateAssetOrigin(form.origin);
      const firstOriginError = Object.values(originErrors)[0];
      if (firstOriginError) {
        setSections((s) => ({ ...s, asset: true }));
        setError(`Asset origin: ${firstOriginError}`);
        return;
      }
    }
    try {
      const next: Project = {
        ...project,
        name: trimmed,
        ...(store.mode === 'free' && { deployTarget: form.target, assetOrigin: form.origin }),
        ...(store.mode === 'cloud' && {
          deployTargetId: form.deployTargetId,
          assetOriginId: form.assetOriginId,
        }),
      };
      const saved = await store.save(next);
      toast('Project settings saved.', 'success');
      onSaved(saved);
      onClose();
    } catch (err) {
      if (err instanceof VersionConflictError) {
        setError('Project was edited elsewhere. Reload to see the latest version.');
        return;
      }
      setError(err instanceof Error ? err.message : 'Failed to save settings.');
    }
  };

  return (
    <dialog ref={dialogRef} className="project-settings-dialog" aria-labelledby="ps-title">
      <form
        method="dialog"
        className="project-settings-form"
        onSubmit={(e) => {
          e.preventDefault();
          void handleSave();
        }}
      >
        <header className="project-settings-header">
          <h2 id="ps-title" className="project-settings-title">
            Project settings
          </h2>
          <button
            type="button"
            className="project-settings-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className="project-settings-body">
          {!caps.canConfigure && (
            <p className="project-settings-hint">
              You can view these settings but only a project maintainer or org admin can change
              them.
            </p>
          )}
          <NameField
            value={form.name}
            readOnly={!caps.canConfigure}
            onChange={(name) => setForm((f) => ({ ...f, name }))}
          />

          {store.mode === 'free' ? (
            <>
              <SettingsSection
                title="Deploy target"
                badge={form.target ? labelDeployKind(form.target.kind) : 'None'}
                badgeIsSet={form.target !== undefined}
                open={sections.deploy}
                onToggle={(o) => setSections((s) => ({ ...s, deploy: o }))}
              >
                <DeployTargetField
                  target={form.target}
                  onChange={(target) => setForm((f) => ({ ...f, target }))}
                  webhookCloudOnly
                />
              </SettingsSection>

              <SettingsSection
                title="Asset origin"
                badge={form.origin ? labelAssetKind(form.origin.kind) : 'None'}
                badgeIsSet={form.origin !== undefined}
                open={sections.asset}
                onToggle={(o) => setSections((s) => ({ ...s, asset: o }))}
              >
                <AssetOriginField
                  origin={form.origin}
                  onChange={(origin) => setForm((f) => ({ ...f, origin }))}
                />
              </SettingsSection>
            </>
          ) : (
            store.orgSlug && (
              <>
                <SettingsSection
                  title="Deploy target"
                  badge={form.deployTargetId ? 'Linked' : 'None'}
                  badgeIsSet={form.deployTargetId !== null}
                  open={sections.deploy}
                  onToggle={(o) => setSections((s) => ({ ...s, deploy: o }))}
                >
                  <DeployTargetPicker
                    orgSlug={store.orgSlug}
                    value={form.deployTargetId}
                    onChange={(id) => setForm((f) => ({ ...f, deployTargetId: id }))}
                  />
                </SettingsSection>

                <SettingsSection
                  title="Asset origin"
                  badge={form.assetOriginId ? 'Linked' : 'None'}
                  badgeIsSet={form.assetOriginId !== null}
                  open={sections.asset}
                  onToggle={(o) => setSections((s) => ({ ...s, asset: o }))}
                >
                  <AssetOriginPicker
                    orgSlug={store.orgSlug}
                    value={form.assetOriginId}
                    onChange={(id) => setForm((f) => ({ ...f, assetOriginId: id }))}
                  />
                </SettingsSection>

                {api && (
                  <SettingsSection
                    title="Members"
                    badge="Roles"
                    badgeIsSet={false}
                    open={membersOpen}
                    onToggle={setMembersOpen}
                  >
                    <ProjectMembersPanel
                      api={api}
                      projectId={project.id}
                      canManage={caps.canManageMembers}
                    />
                  </SettingsSection>
                )}
              </>
            )
          )}

          {error && (
            <p className="project-settings-error" role="alert">
              {error}
            </p>
          )}
        </div>

        <footer className="project-settings-footer">
          <button type="button" className="landing-button-text" onClick={onClose}>
            {caps.canConfigure ? 'Cancel' : 'Close'}
          </button>
          {caps.canConfigure && (
            <button type="submit" className="landing-button-primary">
              Save changes
            </button>
          )}
        </footer>
      </form>
    </dialog>
  );
}

/* ---------------- sub-fields ---------------- */

/**
 * Collapsible section in the settings dialog. Native <details>/<summary>
 * for the disclosure widget so a11y comes for free; controlled open
 * state so the parent can auto-expand on validation errors and reset
 * when the dialog opens on a different project.
 */
export function SettingsSection({
  title,
  badge,
  badgeIsSet,
  open,
  onToggle,
  children,
}: {
  title: string;
  badge: string;
  badgeIsSet: boolean;
  open: boolean;
  onToggle: (open: boolean) => void;
  children: ReactNode;
}): JSX.Element {
  return (
    <details
      className="project-settings-section"
      open={open}
      onToggle={(e) => onToggle(e.currentTarget.open)}
    >
      <summary className="project-settings-section-summary">
        <span className="project-settings-section-title">{title}</span>
        <span className={'project-settings-section-badge' + (badgeIsSet ? ' is-set' : '')}>
          {badge}
        </span>
      </summary>
      <div className="project-settings-section-body">{children}</div>
    </details>
  );
}

function NameField({
  value,
  onChange,
  readOnly = false,
}: {
  value: string;
  onChange: (v: string) => void;
  readOnly?: boolean;
}): JSX.Element {
  const id = useId();
  return (
    <div className="landing-field">
      <label className="landing-label" htmlFor={id}>
        Project name
      </label>
      <input
        id={id}
        type="text"
        className="input"
        value={value}
        readOnly={readOnly}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
